import { inject } from '@angular/core';
import { CanMatchFn, Router } from '@angular/router';
import { rememberNext } from '../../auth/access/access-next';
import { afterFirstSignInStep } from '../../auth/pages/landing-route';
import { Company } from '../../auth/models/company/company';
import { User } from '../../auth/models/user/user';
import { v2LegacyLandingFor, v2SupportsPersona } from '../nav/v2-nav.config';

/**
 * Fails closed, on two counts. A user whose company is not entitled can never
 * route into /v2, even by typing the URL; and neither can a user whose persona
 * has no v2 pages — only the buyer set has been rebuilt, so a supplier or an
 * admin would be handed a bar of pure "classic" links.
 *
 * Both refusals land on the same place: the person's own legacy landing route,
 * which is a page they can actually use rather than a generic bounce.
 *
 * This is a `CanMatchFn` rather than a `CanActivateFn` on purpose: refusing at
 * match time means the lazy chunk is never even fetched for an unentitled
 * user, and the router falls through to the next matching route instead of
 * rendering an empty shell.
 */
export const v2EntitlementGuard: CanMatchFn = (_route, segments) => {
    const company = inject(Company);
    const user = inject(User);
    const router = inject(Router);

    // Signed in, with the second factor when the user has one: every API view answers 403
    // to a password-only token, so the shell would open with every call failing (a token
    // stored before two-step was required). Code step if the password step is done, else
    // sign-in; either way back to the asked-for page afterwards.
    if (!user.isAuthenticated()) {
        // The whole URL asked for, query included (a tutorial link's `?t=`); the matched
        // segments alone drop the query, so they are only the fallback.
        const navigation = router.currentNavigation?.();
        rememberNext(navigation
            ? router.serializeUrl(navigation.extractedUrl)
            : '/' + segments.map(s => s.path).join('/'));
        return router.parseUrl(user.isAuthenticatedBase()
            ? afterFirstSignInStep(user, company)[0]
            : '/access/login');
    }

    if (company.settings?.v2_ui_enabled !== true) {
        return router.parseUrl(v2LegacyLandingFor(user, company));
    }

    if (!v2SupportsPersona(user, company)) {
        return router.parseUrl(v2LegacyLandingFor(user, company));
    }

    return true;
};
